import { useState, useCallback, useMemo } from 'react';
import { clsx } from 'clsx';
import { IJxtTree, IJxtTreeNode } from './types';
import { toggleNode } from './helpers';
import { TreeNode } from './components/TreeNode';

const JxtTree = ({ nodes, checkedValue, onCheckedChange, expandedValue, onExpandedChange, className }: IJxtTree) => {
  // Internal expand state — used when the parent does not control it.
  const [internalExpanded, setInternalExpanded] = useState<string[]>([]);

  const isControlledExpand = expandedValue !== undefined && onExpandedChange !== undefined;
  const expanded = useMemo(
    () => (isControlledExpand ? (expandedValue ?? []) : internalExpanded),
    [isControlledExpand, expandedValue, internalExpanded]
  );

  const handleToggleExpanded = useCallback(
    (value: string) => {
      const next = expanded.includes(value) ? expanded.filter((v) => v !== value) : [...expanded, value];
      if (isControlledExpand) {
        onExpandedChange?.(next);
      } else {
        setInternalExpanded(next);
      }
    },
    [expanded, isControlledExpand, onExpandedChange]
  );

  const handleToggleChecked = useCallback(
    (node: IJxtTreeNode) => {
      onCheckedChange(toggleNode(node, checkedValue));
    },
    [checkedValue, onCheckedChange]
  );

  // Stable key for the node list (avoids full remounts when nodes haven't structurally changed)
  const nodeKeys = useMemo(() => nodes.map((n) => n.value).join(','), [nodes]);

  return (
    <div className={clsx('flex flex-col gap-2', className)}>
      <ul key={nodeKeys} className="flex flex-col gap-2 list-none p-0 m-0">
        {nodes.map((node) => (
          <TreeNode
            key={node.value}
            node={node}
            checkedValue={checkedValue}
            expandedValue={expanded}
            onToggleChecked={handleToggleChecked}
            onToggleExpanded={handleToggleExpanded}
            depth={0}
          />
        ))}
      </ul>
    </div>
  );
};

export default JxtTree;
